//
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import React, { Suspense, useEffect, useRef, useState } from "react";
import { lazily } from "react-lazily";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
  useParams,
  useSearchParams,
} from "react-router-dom";
import "./App.scss";
import EmbeddedTripsBridge from "./components/EmbeddedTripsBridge";
import { isEmbeddedTrips } from "./utils/embeddedTrips";
import { useGlobalState } from "./GlobalStates";
import { Header } from "./components/header/header";
import DownloadAppFooter from "./components/layout/DownloadAppFooter";
import { AppTabBarSpacing } from "./components/layout/appTabBar";
import ActiveTripBanner from "./components/layout/ActiveTripBanner";
import SocialFloatingWidgets from "./components/common/SocialFloatingWidgets";
import ScrollToTop from "./customHooks/useScrollToTop";
import SofestVisitTracker from "./pages/sofest/components/SofestVisitTracker";
import LinkClickTracker from "./components/LinkClickTracker";
import LoadingView from "./components/elements/LoadingView";
import { LoginModal } from "./components/newLogin/LoginModal";
import { LoginWidget } from "./components/newLogin/LoginWidget";

import { getUserData } from "./services/user";
import { captureFrontendException, identifyUser } from "./sentry/init";

import GooglePageViewTracker from "./GooglePageViewTracker";

import axios from "./axios";
import { getOngoingPromotions } from "./services/trip";
import { flushPendingDuckInvite } from "./utils/duckInvite";
import ScrollReveal from "./utils/ScrollReveal";

import i18n, { i18nReady } from "./i18n";
import { safePosthog } from "./utils/conditionalAnalytics";

import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { AlertBanner } from "./components/elements/AlertBanner";
import Destination from "./intro/Destination";
import LandingNew from "./intro/LandingNew";
import { APP_HOME } from "./utils/appShell";
import AffiliateProgram from "./pages/affiliate/AffiliateProgram";
import Campaign from "./pages/campaign/Campaign";
import CompanyInformation from "./pages/extras/CompanyInformation";
import SocialLinks from "./pages/extras/SocialLinks";
import Ideas from "./pages/ideas/Ideas";
import SinglesInferno from "./pages/singlesInferno/SinglesInferno";
import BringYourGuides from "./pages/bringYourGuides/BringYourGuides";
import Outside from "./pages/externalBrands/Outside";

import GroupLeaderInfo from "./pages/leader/GroupLeaderInfo";
import GroupLeaderApplication from "./pages/leader/GroupLeaderApplication";
import CourseList from "./pages/courses/CourseList";
import CourseTaker from "./pages/courses/CourseTaker";
import CourseEditor from "./pages/courses/CourseEditor";
import CourseEnrollments from "./pages/courses/CourseEnrollments";
import CoursePreview from "./pages/courses/CoursePreview";
import LeaderApplicationApply from "./pages/leader/LeaderApplicationApply";
import LeaderProgramme from "./intro/LeaderProgramme";
import LeaderProgrammeOptOut from "./intro/LeaderProgrammeOptOut";
import SotravelXInfo from "./pages/sotravelX/SotravelXInfo";
import LeaderInvitation from "./intro/LeaderInvitation";
import TestCalendar from "./pages/test-calendar";
import Connections from "./profile/Connections/Connections";
import ImportContacts from "./profile/Connections/ImportContacts";
import Privacy from "./profile/Privacy";
import AccountSettings from "./profile/AccountSettings";
import TempLine from "./pages/temp/templine";
import TempAccountMerge from "./pages/temp/tempAccountMerge";

import { countryWithLandingPage, LEGACY_GUIDE_REDIRECTS } from "./utils/constants";
import LegacyGuideRedirect from "./components/common/LegacyGuideRedirect";
import {
  astroTripHandoffUrl,
  loadAstroTripsRollout,
  prepareTripRouteHandoff,
  rememberListLocation,
} from "./utils/astroTrips";

import { useScrollPreservation } from "./hooks/useScrollPreservation";
import { useIsInApp } from "./customHooks/useIsInApp";

import { I18nextProvider } from "react-i18next";
import LinkToAppLeaderDiaryDetails from "./pages/appLinks/LinkToAppLeaderDiaryDetails";

const SofestLanding = React.lazy(() => import("./pages/sofest/SofestLanding"));
const SofestPillars = React.lazy(() => import("./pages/sofest/SofestPillars"));
const SofestTrips = React.lazy(() => import("./pages/sofest/SofestTrips"));
const SofestTripDetail = React.lazy(() => import("./pages/sofest/SofestTripDetail"));
const FormList = React.lazy(() => import("./pages/forms/FormList"));
const FormEditor = React.lazy(() => import("./pages/forms/FormEditor"));
const FormSubmissions = React.lazy(() => import("./pages/forms/FormSubmissions"));
const MyReferrals = React.lazy(() => import("./pages/myReferrals/MyReferrals"));
const ChangeCard = React.lazy(() => import("./pages/reserve/changeCard/ChangeCard"));
const HostProfile = React.lazy(() => import("./pages/host/Profile/HostProfile"));
// Sotravel Club platform (self-serve local events)
const ClubCreation = React.lazy(() => import("./pages/club/creation/ClubCreation"));
const ManageLayout = React.lazy(() => import("./pages/manage/ManageLayout"));
const ManageEvents = React.lazy(() => import("./pages/manage/ManageEvents"));
const EventCreate = React.lazy(() => import("./pages/manage/EventCreate"));
const EventManage = React.lazy(() => import("./pages/manage/EventManage"));
const TripCreate = React.lazy(() => import("./pages/manage/TripCreate"));
const TripManage = React.lazy(() => import("./pages/manage/TripManage"));
const ManageSales = React.lazy(() => import("./pages/manage/ManageSales"));
const ManageClubs = React.lazy(() => import("./pages/manage/ManageClubs"));
const ManagePayouts = React.lazy(() => import("./pages/manage/ManagePayouts"));
const ManageSettings = React.lazy(() => import("./pages/manage/ManageSettings"));
const ManageClub = React.lazy(() => import("./pages/manage/ManageClub"));
const MongoliaSignature = React.lazy(() => import("./pages/signature/MongoliaSignature"));
const StansSignature = React.lazy(() => import("./pages/signature/StansSignature"));
const MongoliaJournal = React.lazy(() => import("./pages/signature/MongoliaJournal"));
const StansJournal = React.lazy(() => import("./pages/signature/StansJournal"));
const SkiRewardPage = React.lazy(() => import("./game/ski/SkiRewardPage"));

let stripePromise;
// if (localStorage.getItem("currency") == "myr") {
//   stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY_MY);
// } else {
//   stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);
// }

stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const Landing = React.lazy(() => import("./intro/Landing"));
const LeaderOnlineInterview = React.lazy(() => import("./pages/sensei/LeaderOnlineInterview"));
const LandingScreen = React.lazy(() => import("./pages/sensei/screens/LandingScreen"));
const SelectDuckiesScreen = React.lazy(() => import("./pages/sensei/screens/SelectDuckiesScreen"));
const FirstEvalScreen = React.lazy(() => import("./pages/sensei/screens/FirstEvalScreen"));
const ConfirmSelectionScreen = React.lazy(
  () => import("./pages/sensei/screens/ConfirmSelectionScreen")
);
const InterviewScreen = React.lazy(() => import("./pages/sensei/screens/InterviewScreen"));
const FinalEvalScreen = React.lazy(() => import("./pages/sensei/screens/FinalEvalScreen"));
const WelcomeDiscount = React.lazy(() => import("./intro/WelcomeDiscount"));
const WelcomeDiscountV2 = React.lazy(() => import("./intro/WelcomeDiscountV2"));
const DuckFamDiscount = React.lazy(() => import("./intro/DuckFamDiscount"));
const CommunityPage = React.lazy(() => import("./intro/CommunityPage"));
const CommunityPageV2 = React.lazy(() => import("./intro/CommunityPageV2"));
const AboutV2 = React.lazy(() => import("./home/AboutV2"));
const PolicyV2 = React.lazy(() => import("./components/privacy-policy/PolicyV2"));
const TermsAppV2 = React.lazy(() => import("./components/privacy-policy/TermsAppV2"));
const PolicyAppV2 = React.lazy(() => import("./components/privacy-policy/PolicyAppV2"));
const HostReviewStatusV2 = React.lazy(() => import("./pages/host/HostReview/HostReviewStatusV2"));
const HostReviewFormV2 = React.lazy(() =>
  import("./pages/host/HostReview/HostReviewFormV2").then((module) => ({
    default: module.HostReviewFormV2,
  }))
);

const NotFoundV2 = React.lazy(() =>
  import("./pages/NotFoundV2").then((module) => ({
    default: module.NotFoundV2,
  }))
);

const UserProfile = React.lazy(() => import("./profile/UserProfile"));
const MyTripActions = React.lazy(() => import("./myTrips/MyTripActions"));
const MyActivityActions = React.lazy(() => import("./myActivities/MyActivityActions"));
const MyTripUpload = React.lazy(() => import("./myTrips/MyTripUpload"));
const MyTripItinerary = React.lazy(() => import("./myTrips/MyTripItinerary"));
const MyTripReview = React.lazy(() => import("./myTrips/MyTripReview"));
const MyTripItinerarySubpage = React.lazy(() => import("./myTrips/MyTripItinerarySubpage"));
const CheckInPreview = React.lazy(() => import("./pages/checkin/CheckInPreview"));
const CheckInAttendance = React.lazy(() => import("./pages/checkin/CheckInAttendance"));
// Using lazily for named imports
const { Home } = lazily(() => import("./home/Home"));
const { About } = lazily(() => import("./home/About"));
const { Trip } = lazily(() => import("./pages/trip/Trip"));
const { Book } = lazily(() => import("./pages/reserve/Book"));
const ReserveRoute = () => {
  const [searchParams] = useSearchParams();
  return searchParams.get("trip_id") === "3548" ? (
    <Navigate to="/sofest/trips/mongolia-summer-festival" replace />
  ) : (
    <Book />
  );
};
// /trips/:project_id, /trip/:trip_id, /t/:project_slug. The location this
// document was loaded on always renders <Trip /> (Astro already chose this
// app for it); an in-app navigation to a project Astro renders becomes a full
// load. location.replace: the router already pushed this entry.
const TripRoute = () => {
  const location = useLocation();
  const { project_id } = useParams();
  const decision = useRef(null);
  if (decision.current?.key !== location.key) {
    decision.current = {
      key: location.key,
      url: astroTripHandoffUrl(location, project_id ?? location.state?.tripProjectId),
    };
  }
  const { url } = decision.current;
  useEffect(() => {
    if (!url) return;
    prepareTripRouteHandoff(url);
    window.location.replace(url);
  }, [url]);
  return url ? <LoadingView /> : <Trip />;
};
// The last non-trip URL, for the back hint TripRoute leaves the Astro page.
const TripListTracker = () => {
  const location = useLocation();
  useEffect(() => rememberListLocation(location), [location]);
  return null;
};
// "/" is the marketing home on the website. The app has no marketing site —
// its home is the Explore feed — so there every link that lands on "/" (guide
// page logos, "Go to homepage" / "Back to home" buttons, logins without a
// reroute…) goes to /all-trips instead, keeping ?query and #hash. `replace`
// so back doesn't bounce onto "/" again.
const HomeRoute = () => {
  const { search, hash } = useLocation();
  const inApp = useIsInApp();
  return inApp ? <Navigate to={{ pathname: APP_HOME, search, hash }} replace /> : <LandingNew />;
};
const { Create } = lazily(() => import("./pages/reserve/Create"));
const { ClubApp } = lazily(() => import("./home/ClubApp"));
const { Event } = lazily(() => import("./pages/event/Event"));
// /club/:slug is shared: all-digit slugs are Soclub activities (Event), any
// other slug is a club handle (HostProfile renders the club page). Club
// handles always contain a letter (BE HANDLE_RE + isHandleAvailable).
const ClubSlugRoute = () => {
  const { activity_id } = useParams();
  return /^\d+$/.test(String(activity_id || "")) ? <Event /> : <HostProfile handle={activity_id} />;
};
const { SoclubBook } = lazily(() => import("./pages/reserve/SoclubBook"));
const FormPage = React.lazy(() => import("./pages/form/FormPage"));

const { Confirm } = lazily(() => import("./pages/booked/Confirm"));
const { ConfirmEventBooking } = lazily(() => import("./pages/booked/ConfirmEventBooking"));
const { Refund } = lazily(() => import("./pages/booked/Refund"));
const { ProjectS } = lazily(() => import("./projectS/ProjectS"));
const { DoMore } = lazily(() => import("./projectS/DoMore"));

const { LoginPage } = lazily(() => import("./components/login/LoginPage"));
const { AppTelegramLogin } = lazily(() => import("./components/login/AppTelegramLogin"));
const { AppLineLogin } = lazily(() => import("./components/login/AppLineLogin"));

//AppLinks
const LinkToAppExplore = React.lazy(() => import("./pages/appLinks/LinkToAppExplore"));
const LinkToAppProfile = React.lazy(() => import("./pages/appLinks/LinkToAppProfile"));
const LinkToAppSamuraiInvitation = React.lazy(
  () => import("./pages/appLinks/LinkToAppSamuraiInvitation")
);
const LinkToAppSoclub = React.lazy(() => import("./pages/appLinks/LinkToAppSoclub"));
const LinkToAppChat = React.lazy(() => import("./pages/appLinks/LinkToAppChat"));
const LinkToReserve = React.lazy(() => import("./pages/appLinks/LinkToReserve"));
const LinkToAppTripDetails = React.lazy(() => import("./pages/appLinks/LinkToAppTripDetails"));
const LinkToAppSoclubUserActivityDetails = React.lazy(
  () => import("./pages/appLinks/LinkToAppSoclubUserActivityDetails")
);
const LinkToAppMerchantBookings = React.lazy(
  () => import("./pages/appLinks/LinkToAppMerchantBookings")
);

const { LoginForAccess } = lazily(() => import("./components/loginForAccess/LoginForAccess"));
const { ClaimDiscount } = lazily(() => import("./components/loginForAccess/ClaimDiscount"));

const { Profile } = lazily(() => import("./profile/Profile"));
const { Policy } = lazily(() => import("./components/privacy-policy/Policy"));
const DuckProfile = React.lazy(() => import("./pages/profile/DuckProfile"));
const LeaderTripReviews = React.lazy(() => import("./pages/profile/LeaderTripReviews"));
const { PolicyApp } = lazily(() => import("./components/privacy-policy/PolicyApp"));
const { TermsApp } = lazily(() => import("./components/privacy-policy/TermsApp"));

const { MyTrips } = lazily(() => import("./myTrips/MyTrips"));

const { HostForm } = lazily(() => import("./pages/host/HostForm"));
const HostFormV2 = React.lazy(() => import("./pages/host/HostForm/HostFormV2"));
const { HostReviewForm } = lazily(() => import("./pages/host/HostReview/HostReviewForm"));
const { HostReviewStatus } = lazily(() => import("./pages/host/HostReview/HostReviewStatus"));

const NotifEditV2 = React.lazy(() =>
  import("./profile/NotifEditV2").then((module) => ({
    default: module.NotifEditV2,
  }))
);

const { AffiliateRequest } = lazily(() => import("./components/affiliate/AffiliateRequest"));

const FAQ = React.lazy(() => import("./pages/faq/FAQ"));

// Ongoing promotions refresh (see the promotions effect in App).
const PROMOTIONS_REFRESH_MS = 5 * 60 * 1000;
// Routes that render Book (checkout), where BookPromoCode writes into the map.
const CHECKOUT_PATHS = ["/reserve", "/new"];

loadAstroTripsRollout();

function App() {
  const isInApp = useIsInApp();
  if (process.env.NODE_ENV === "development") {
    // init dev stuff
  } else {
    // clarity.init("mmnx5isd28");
    // Only initialize PostHog if not in China
    safePosthog.init("phc_yqvnkXnyjUZacqkVtAbWUGf9TB4ghnziU0CwiM37VMH", {
      api_host: "https://us.i.posthog.com",
      person_profiles: "identified_only", // or 'always' to create profiles for anonymous users as well
    });
  }

  // Wait for i18n to be initialized before rendering so that i18n.language
  // is set before any component renders or SWR fires API calls.
  const [isI18nReady, setIsI18nReady] = useState(false);
  useEffect(() => {
    i18nReady.then(() => setIsI18nReady(true)).catch(() => setIsI18nReady(true));
  }, []);

  // Native webviews reserve a visible scrollbar gutter on some devices. Keep
  // scrolling intact but remove that gutter while the site runs in the app.
  useEffect(() => {
    if (!isInApp) return undefined;

    document.documentElement.classList.add("in-app-webview");
    return () => document.documentElement.classList.remove("in-app-webview");
  }, [isInApp]);

  const [user, setUser] = useGlobalState("user");
  const [userLoading, setUserLoading] = useGlobalState("userLoading");
  const [, setUserFetchError] = useGlobalState("userFetchError");
  const [, setPromotion] = useGlobalState("promotion");

  const homeAllTripsRef = useRef(null);

  const scrollRevealRef = useRef(null);

  // User sign in
  useEffect(() => {
    // Set dev user id and jwt token for convenience

    if (process.env.NODE_ENV === "development") {
      // process.env.REACT_APP_DEV_USER_ID
      //   ? localStorage.setItem("USER_ID", process.env.REACT_APP_DEV_USER_ID)
      //   : localStorage.removeItem("USER_ID");
      // process.env.REACT_APP_DEV_JWT_TOKEN
      //   ? localStorage.setItem("JWT_TOKEN", process.env.REACT_APP_DEV_JWT_TOKEN)
      //   : localStorage.removeItem("JWT_TOKEN");
      // Skip the dev convenience auto-login when the page is driven by automated
      // browser tests — Playwright/WebDriver sets navigator.webdriver, and the
      // e2e suite also seeds E2E_SKIP_DEV_AUTOLOGIN. This lets the injected
      // test-user session (storageState JWT_TOKEN/USER_ID) stand instead of being
      // overwritten by the dev user. No effect in production (block is dev-only).
      const isAutomatedTest =
        (typeof navigator !== "undefined" && navigator.webdriver) ||
        localStorage.getItem("E2E_SKIP_DEV_AUTOLOGIN") === "true";

      if (!isAutomatedTest) {
        process.env.REACT_APP_DEV_USER_ID &&
          localStorage.setItem("USER_ID", process.env.REACT_APP_DEV_USER_ID);
        process.env.REACT_APP_DEV_JWT_TOKEN &&
          localStorage.setItem("JWT_TOKEN", process.env.REACT_APP_DEV_JWT_TOKEN);
      }
    }
    const getUser = async () => {
      const jwt_token = localStorage.getItem("JWT_TOKEN");
      if (!jwt_token) {
        setUserLoading(false);
        return;
      }
      // Retry NON-401 failures. A transient backend/proxy blip (502/504/timeout/
      // network) must NOT drop a token-holder to logged-out state — that is the
      // root cause of the intermittent "asked to log in again on checkout"
      // report. A genuine 401 is handled by the authAxios interceptor (clears
      // token + redirects) and must not be retried here.
      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          const res = await getUserData();
          setUser(res.data);
          setUserFetchError(false);
          identifyUser(res.data);
          // Club platform: a club page visited while logged out was tracked by
          // browser id only — attach it to the account so bookings attribute.
          try {
            const pendingClubVisit = localStorage.getItem("club_visit_client_id");
            if (pendingClubVisit) {
              const { claimClubVisits } = await import("./services/club");
              claimClubVisits(pendingClubVisit)
                .then(() => localStorage.removeItem("club_visit_client_id"))
                .catch(() => {});
            }
          } catch (e) {
            /* storage unavailable */
          }
          safePosthog.identify(res.data.id, {
            email: res.data.email,
            name: res.data.first_name,
          });
          break;
        } catch (err) {
          if (err?.response?.status === 401) break; // interceptor already cleared the token
          if (attempt < 3) {
            await new Promise((r) => setTimeout(r, 500 * attempt));
            continue;
          }
          // Exhausted retries on a transient error: keep the token, flag the
          // failure (so the checkout login gate shows a Retry affordance, not
          // the login form), and surface it to Sentry for monitoring.
          console.warn("getUser hydration failed after retries", err?.message);
          setUserFetchError(true);
          captureFrontendException(err, {
            tags: {
              area: "getUser_hydration",
              status: err?.response?.status ?? "network",
            },
            level: "warning",
          });
        }
      }
      setUserLoading(false);
    };

    document.body.classList.add("is-loaded");
    scrollRevealRef.current?.init();
    getUser();
  }, [setUser, setUserLoading, setUserFetchError]);

  useEffect(() => {
    if (userLoading) return;

    const abortController = new AbortController();
    let lastFetchedAt = 0;
    const getCurrentPromotions = async () => {
      lastFetchedAt = Date.now();
      try {
        // A duck-fam link opened while signed out is saved now, before the
        // lookup, so its discount shows and follows the guest to the app.
        // Capped so a slow network never holds the promotions back.
        if (user?.id) {
          await Promise.race([
            flushPendingDuckInvite(),
            new Promise((resolve) => setTimeout(resolve, 3000)),
          ]);
        }
        const data = await getOngoingPromotions({
          userId: user?.id ?? "",
          duckCode: sessionStorage.getItem("duck_code") ?? null,
          duckRedirectTrip: sessionStorage.getItem("duck_redirect_trip") ?? null,
          signal: abortController.signal,
        });
        if (data) {
          setPromotion(data);
        }
      } catch (error) {
        if (error.name !== "AbortError" && error.name !== "CanceledError") {
          console.error("Error fetching promotions:", error);
        }
      }
    };

    getCurrentPromotions();

    // Trip date lists (SWR) refresh when the tab regains focus, but this map was
    // only fetched on load — so a tab left open showed a date created after load
    // without its auto early-bird price (trip 3734, Oct 2026). Refetch when the
    // visitor comes back, at most every 5 minutes: the endpoint isn't cached and
    // works over every future trip. Never on checkout, where BookPromoCode adds
    // an applied code to this map and a refetch would drop it.
    const refreshIfStale = () => {
      if (document.visibilityState !== "visible") return;
      if (Date.now() - lastFetchedAt < PROMOTIONS_REFRESH_MS) return;
      if (CHECKOUT_PATHS.includes(window.location.pathname.replace(/\/$/, ""))) return;
      getCurrentPromotions();
    };
    window.addEventListener("focus", refreshIfStale);
    document.addEventListener("visibilitychange", refreshIfStale);

    return () => {
      abortController.abort();
      window.removeEventListener("focus", refreshIfStale);
      document.removeEventListener("visibilitychange", refreshIfStale);
    };
  }, [user, userLoading]);

  useEffect(() => {
    scrollRevealRef.current?.init();
  }, [window.location.href]);

  // Capture a shared ?promo=<code> link once on load: stash the code for auto-apply
  // at checkout (BookPromoCode resolves + applies it), clear any stale resolved codes
  // from a prior session so they can't leak into this booking's charge, and strip the
  // param from the URL. The charge still re-validates server-side, so this is display-side.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const promo = params.get("promo");
    if (!promo) return;
    sessionStorage.setItem("pending_promo_code", promo);
    sessionStorage.removeItem("promoter_code");
    sessionStorage.removeItem("duck_code");
    sessionStorage.removeItem("duck_redirect_trip");
    params.delete("promo");
    const search = params.toString();
    window.history.replaceState(
      {},
      "",
      window.location.pathname + (search ? `?${search}` : "") + window.location.hash
    );
  }, []);

  if (!isI18nReady) return null;

  return (
    <I18nextProvider i18n={i18n}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <ScrollReveal
          ref={scrollRevealRef}
          children={() => (
            <Router>
              <EmbeddedTripsBridge />
              <TripListTracker />
              <GooglePageViewTracker />
              <ScrollToTop />
              <SofestVisitTracker />
              <LinkClickTracker />
              <AlertBanner />
              <Header homeAllTripsRef={homeAllTripsRef} />
              {!isEmbeddedTrips && <>
              <AppTabBarSpacing />
              <DownloadAppFooter />
              <ActiveTripBanner />
              <SocialFloatingWidgets />
              <LoginWidget />
              </>}
              <LoginModal />
              <Suspense fallback={<LoadingView />}>
                <Routes>
                  <Route path="/" exact element={<HomeRoute />} />
                  {Object.entries(LEGACY_GUIDE_REDIRECTS).map(([path, to]) => (
                    <Route key={path} path={path} exact element={<LegacyGuideRedirect to={to} />} />
                  ))}
                  {countryWithLandingPage.map((country) => (
                    <Route
                      key={country}
                      path={`/${country}`}
                      exact
                      element={
                        <Elements stripe={stripePromise}>
                          <Destination destinationKey={country} />
                        </Elements>
                      }
                    />
                  ))}
                  <Route path="/welcome-discount" exact element={<WelcomeDiscountV2 />} />
                  <Route path="/ski-reward" exact element={<SkiRewardPage />} />
                  <Route path="/duck-fam-discount" exact element={<DuckFamDiscount />} />
                  <Route path="/welcome-discount-old" exact element={<WelcomeDiscount />} />
                  <Route path="/community-page" exact element={<CommunityPageV2 />} />
                  <Route path="/community-page-old" exact element={<CommunityPage />} />
                  <Route path="/claim-discount" exact element={<ClaimDiscount />} />

                  <Route
                    path="/all-trips"
                    exact
                    element={<Home homeAllTripsRef={homeAllTripsRef} />}
                  />

                  <Route path="/about" element={<AboutV2 />} />
                  <Route path="/about-old" element={<About />} />

                  <Route path="/t/:project_slug" element={<TripRoute />} />
                  <Route path="/trip/:trip_id" element={<TripRoute />} />
                  <Route path="/trips/:project_id" element={<TripRoute />} />

                  <Route path="/host/:user_name" element={<HostProfile />} />
                  <Route path="/form/:key" element={<FormPage />} />
                  <Route path="/reserve" element={<ReserveRoute />} />
                  <Route path="/change-card" element={<ChangeCard />} />
                  <Route path="/new" element={<Book />} />
                  <Route path="/booked" element={<Confirm />} />
                  <Route path="/booked/:trip_id" element={<Confirm />} />
                  <Route path="/cancellation" element={<Refund />} />

                  <Route path="/templine" element={<TempLine />} />
                  <Route path="/temp-account-merge" element={<TempAccountMerge />} />

                  <Route path="/club" element={<ClubApp />} />
                  {/* Club platform: must precede /club/:activity_id or it is swallowed. */}
                  <Route path="/club/creation" element={<ClubCreation />} />
                  <Route path="/manage/*" element={<Navigate to="/club/manage/events" replace />} />
                  <Route path="/club/manage" element={<ManageLayout />}>
                    <Route index element={<Navigate to="/club/manage/events" replace />} />
                    <Route path="events" element={<ManageEvents />} />
                    <Route path="events/new" element={<EventCreate />} />
                    <Route path="events/:tripId/*" element={<EventManage />} />
                    <Route path="trips/new" element={<TripCreate />} />
                    <Route path="trips/:tripId/edit" element={<TripCreate />} />
                    <Route path="trips/:tripId/*" element={<TripManage />} />
                    <Route path="sales" element={<ManageSales />} />
                    <Route path="analytics" element={<Navigate to="/club/manage/sales" replace />} />
                    <Route path="payouts" element={<ManagePayouts />} />
                    <Route path="settings/*" element={<ManageSettings />} />
                    <Route path="club/*" element={<ManageClub />} />
                    <Route path="clubs" element={<ManageClubs />} />
                  </Route>
                  {/* /club/<number> = legacy Soclub activity; /club/<handle> = club page. */}
                  <Route path="/club/:activity_id" element={<ClubSlugRoute />} />
                  <Route path="/soclub/reserve" element={<SoclubBook />} />
                  <Route path="/soclub/booked" element={<ConfirmEventBooking />} />

                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/test-calendar" exact element={<TestCalendar />} />

                  <Route path="/app-login" element={<AppTelegramLogin />} />
                  <Route path="/app-line-login" element={<AppLineLogin />} />

                  <Route path="/courses" element={<CourseList />} />
                  <Route path="/courses/:slug/edit" element={<CourseEditor />} />
                  <Route path="/courses/:slug/enrollments" element={<CourseEnrollments />} />
                  <Route path="/courses/:slug/preview" element={<CoursePreview />} />
                  <Route path="/courses/:slug" element={<CourseTaker />} />

                  <Route path="/group-leader-intro" element={<GroupLeaderInfo />} />
                  <Route path="/group-leader/apply" element={<GroupLeaderApplication />} />
                  <Route
                    path="/our-leader-programme/apply/:batch"
                    element={<LeaderApplicationApply />}
                  />
                  <Route path="/our-leader-programme/apply" element={<LeaderApplicationApply />} />
                  <Route path="/our-leader-programme/opt-out" element={<LeaderProgrammeOptOut />} />
                  <Route path="/our-leader-programme" element={<LeaderProgramme />} />

                  <Route path="/forms" element={<FormList />} />
                  <Route path="/forms/new" element={<FormEditor />} />
                  <Route path="/forms/:id/edit" element={<FormEditor />} />
                  <Route path="/forms/:id/submissions" element={<FormSubmissions />} />

                  <Route path="/sotravel-x" element={<SotravelXInfo />} />

                  <Route path="/signature/mongolia" element={<MongoliaSignature />} />
                  <Route path="/signature/stans" element={<StansSignature />} />
                  <Route path="/signature/mongolia/:articleSlug" element={<MongoliaJournal />} />
                  <Route path="/signature/stans/:articleSlug" element={<StansJournal />} />

                  <Route path="/leader-invitation/:invitation_key" element={<LeaderInvitation />} />
                  <Route path="/sensei/leader-interview" element={<LeaderOnlineInterview />}>
                    <Route index element={<LandingScreen />} />
                    <Route path="first-eval" element={<FirstEvalScreen />} />
                    <Route path="select" element={<SelectDuckiesScreen />} />
                    <Route path="confirm" element={<ConfirmSelectionScreen />} />
                    <Route path="interview" element={<InterviewScreen />} />
                    <Route path="final-eval" element={<FinalEvalScreen />} />
                  </Route>

                  {/* Public listing: /app-redirect/Explore/trip/:id
                      Booked trip: /app-redirect/trip/:id
                      Project listing: /app-redirect/Explore/trips/:id */}

                  <Route path="/app-redirect/Profile/*" element={<LinkToAppProfile />} />

                  <Route
                    path="/app-redirect/samurai-invitation"
                    element={<LinkToAppSamuraiInvitation />}
                  />

                  <Route path="/app-redirect/Explore/*" element={<LinkToAppExplore />} />

                  <Route
                    path="/app-redirect/Soclub/:merchantStoreId?"
                    element={<LinkToAppSoclub />}
                  />
                  {/* reserved booking trip page */}
                  <Route path="/app-redirect/trip/:tripId?" element={<LinkToAppTripDetails />} />
                  <Route
                    path="/app-redirect/trip/:tripId?/updateTripInformation"
                    element={<LinkToAppTripDetails subUrl={"updateTripInformation"} />}
                  />
                  <Route
                    path="/app-redirect/trip/:tripId?/tripInstructions"
                    element={<LinkToAppTripDetails subUrl={"tripInstructions"} />}
                  />
                  <Route
                    path="/app-redirect/trip/:tripId?/viewRoomies"
                    element={<LinkToAppTripDetails subUrl={"viewRoomies"} />}
                  />
                  <Route
                    path="/app-redirect/trip/:tripId?/whosComing"
                    element={<LinkToAppTripDetails subUrl={"whosComing"} />}
                  />
                  <Route
                    path="/app-redirect/trip/:tripId?/splitPayments"
                    element={<LinkToAppTripDetails subUrl={"splitPayments"} />}
                  />
                  <Route
                    path="/app-redirect/trip/:tripId?/settlePayments"
                    element={<LinkToAppTripDetails subUrl={"settlePayments"} />}
                  />
                  {/* Free Reserve "couldn't charge your deposit" notification CTA
                      (BE getCtaLink bookingTab "settleDeposit"). The handler existed
                      in LinkToAppTripDetails since Jul 2026 but the route was never
                      registered, so every such link fell through to /404. */}
                  <Route
                    path="/app-redirect/trip/:tripId?/settleDeposit"
                    element={<LinkToAppTripDetails subUrl={"settleDeposit"} />}
                  />
                  <Route
                    path="/app-redirect/trip/:tripId?/review"
                    element={<LinkToAppTripDetails subUrl={"review"} />}
                  />
                  <Route
                    path="/app-redirect/trip/:tripId?/LeaderDiaryEntry"
                    element={<LinkToAppTripDetails subUrl={"LeaderDiaryEntry"} />}
                  />
                  <Route
                    path="/app-redirect/trip/:tripId?/checkin"
                    element={<LinkToAppTripDetails subUrl={"checkin"} />}
                  />
                  <Route
                    path="/app-redirect/LeaderDiaryDetails/:diaryId?"
                    element={<LinkToAppLeaderDiaryDetails />}
                  />
                  <Route
                    path="/app-redirect/SoclubUserActivityDetails/:activityId?"
                    element={<LinkToAppSoclubUserActivityDetails />}
                  />
                  <Route path="/app-redirect/chat/:chatId?" element={<LinkToAppChat />} />
                  <Route path="/app-redirect/reserve" element={<LinkToReserve />} />
                  <Route
                    path="/app-redirect/event_chat/:chatId?"
                    element={<LinkToAppChat isEventChat={true} />}
                  />
                  <Route
                    path="/app-redirect/soclub_chat/:chatId?"
                    element={<LinkToAppChat isSoclubChat={true} />}
                  />

                  <Route
                    path="/app-redirect/MerchantBookings"
                    element={<LinkToAppMerchantBookings />}
                  />

                  <Route path="/access" element={<LoginForAccess />} />

                  <Route path="/profile" element={<Profile />} />
                  <Route path="/notif-edit" exact element={<NotifEditV2 />} />
                  <Route path="/profile/privacy" element={<Privacy />} />
                  <Route path="/profile/account" element={<AccountSettings />} />
                  <Route path="/profile/connections" element={<Connections />} />
                  <Route path="/profile/connections/import-contacts" element={<ImportContacts />} />
                  <Route path="/profile/duck" element={<DuckProfile />} />
                  <Route path="/profile/duck/reviews" element={<LeaderTripReviews />} />
                  <Route path="/profile/:user_id" element={<UserProfile />} />
                  <Route path="/profile/:user_id/connections" element={<Connections />} />

                  <Route path="/project-s" element={<ProjectS />} />
                  <Route path="/domore" element={<DoMore />} />

                  <Route path="/my-trips" exact element={<MyTrips />} />
                  <Route
                    path="/my-trips/:trip_id/action-items"
                    exact
                    element={
                      <Elements stripe={stripePromise}>
                        <MyTripActions />
                      </Elements>
                    }
                  />
                  <Route
                    path="/my-activities/:activity_id/action-items"
                    exact
                    element={
                      <Elements stripe={stripePromise}>
                        <MyActivityActions />
                      </Elements>
                    }
                  />
                  <Route path="/my-trips/:trip_id/upload" exact element={<MyTripUpload />} />
                  <Route path="/my-projects/:project_id/upload" exact element={<MyTripUpload />} />

                  <Route path="/my-trips/:trip_id/itinerary" exact element={<MyTripItinerary />} />
                  <Route path="/my-trips/:trip_id/review" exact element={<MyTripReview />} />
                  <Route
                    path="/my-trips/:trip_id/attendance"
                    exact
                    element={<CheckInAttendance />}
                  />
                  <Route path="/checkin-preview" exact element={<CheckInPreview />} />
                  <Route
                    path="/my-trips/:trip_id/itinerary/:notion_id"
                    exact
                    element={<MyTripItinerarySubpage />}
                  />

                  <Route path="/trip-starter-form" element={<HostFormV2 />} />
                  <Route path="/trip-starter-form-old" element={<HostForm />} />
                  <Route path="/user-review-status" exact element={<HostReviewStatusV2 />} />
                  <Route path="/add-review/:tele_username" exact element={<HostReviewFormV2 />} />
                  <Route path="/add-review-old/:tele_username" exact element={<HostReviewForm />} />
                  <Route path="/user-review-status-old" exact element={<HostReviewStatus />} />

                  <Route path="/privacy-policy" element={<PolicyV2 />} />
                  <Route path="/privacy-policy-old" element={<Policy />} />
                  <Route path="/privacy-policy-app" element={<PolicyAppV2 />} />
                  <Route path="/privacy-policy-app-old" element={<PolicyApp />} />
                  <Route path="/terms-app" element={<TermsAppV2 />} />
                  <Route path="/terms-app-old" element={<TermsApp />} />

                  <Route path="/ideas" element={<Ideas />} />
                  {/* <Route path="/c/:campaignSlug" element={<Campaign />} /> */}
                  <Route
                    path="/c/:campaignSlug"
                    exact
                    element={
                      <Elements stripe={stripePromise}>
                        <Campaign />
                      </Elements>
                    }
                  />
                  <Route path="/inferno" element={<SinglesInferno />} />
                  <Route path="/bring-your-guides" element={<BringYourGuides />} />

                  <Route path="/affiliate" element={<AffiliateProgram />} />
                  <Route path="/my-referrals" element={<MyReferrals />} />
                  <Route path="/request-affiliate" element={<AffiliateRequest />} />
                  <Route path="/socials" element={<SocialLinks />} />
                  <Route path="/company-information" element={<CompanyInformation />} />

                  <Route element={<Home />} />

                  {/* Old SoFest landing — redirect to /sofest to consolidate SEO. */}
                  <Route
                    path="/sofest-mountain-of-madness"
                    element={<Navigate to="/sofest" replace />}
                  />

                  <Route path="/sofest" element={<SofestLanding />} />
                  <Route path="/sofest/pillars" element={<SofestPillars />} />
                  <Route path="/sofest/trips" element={<SofestTrips />} />
                  <Route
                    path="/sofest/trips/mongolia-mini-naadam"
                    element={<Navigate to="/sofest/trips/mongolia-summer-festival" replace />}
                  />
                  <Route path="/sofest/trips/:id" element={<SofestTripDetail />} />

                  <Route path="/outside" element={<Outside />} />
                  {/* Dedicated 404 route */}
                  <Route path="/404" element={<NotFoundV2 />} />

                  <Route path="/faq" exact element={<FAQ />} />

                  {/* Catch-all route that redirects to the 404 page */}
                  <Route
                    path="*"
                    element={
                      <Navigate
                        to="/404"
                        replace
                        state={{
                          from: window.location.pathname + window.location.search,
                        }}
                      />
                    }
                  />
                </Routes>
              </Suspense>
            </Router>
          )}
        />
      </LocalizationProvider>
    </I18nextProvider>
  );
}

export default App;
